import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { formatDateColombia } from '../utils/constants';
import Icon from '../components/atoms/Icon';

const useAdminUsers = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const cargarUsuarios = useCallback(async (searchTerm) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('auth_token');
      const url = searchTerm
        ? `/api/admin/usuariosweb?nombreEmpresa=${encodeURIComponent(searchTerm)}`
        : '/api/admin/usuariosweb';

      const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error('No se pudieron cargar los usuarios.');
      const data = await response.json();
      setUsuarios(data.usuarios || []);
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      cargarUsuarios(filtro);
    }, 300);
    return () => clearTimeout(handler);
  }, [filtro, cargarUsuarios]);

  const actualizarCorreoUsuario = async (userId, nuevoCorreo) => {
    if (!nuevoCorreo.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nuevoCorreo)) {
      toast.error('Por favor, ingrese un correo válido.');
      return false;
    }
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/admin/usuariosweb/${userId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ correo: nuevoCorreo })
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || 'Error al actualizar.');
      }
      toast.success('Correo actualizado.');
      await cargarUsuarios(filtro);
      return true;
    } catch (err) {
      toast.error(err.message);
      return false;
    }
  };

  return { usuarios, loading, error, filtro, setFiltro, actualizarCorreoUsuario };
};

const SearchFilter = ({ filtro, setFiltro, disabled }) => (
  <div className="relative">
    <Icon name="search" className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
    <input
      type="text"
      value={filtro}
      onChange={(e) => setFiltro(e.target.value)}
      disabled={disabled}
      className="w-full pl-11 pr-10 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ec-primary-500"
      placeholder="Buscar por nombre de empresa..."
    />
    {filtro && (
      <button onClick={() => setFiltro('')} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700">
        <Icon name="x-circle" className="h-5 w-5" />
      </button>
    )}
  </div>
);

const UserCard = ({ usuario, isEditing, onEdit, onSave, onCancel, nuevoCorreo, setNuevoCorreo }) => (
  <div className={`bg-white rounded-lg border p-4 transition-all duration-300 ${isEditing ? 'border-ec-primary-300 shadow-md' : 'border-gray-200'}`}>
    <div className="flex justify-between items-start">
      <div>
        <p className="font-bold text-gray-900 break-words">{usuario.NOMBRE_EMPRESA || 'N/A'}</p>
        <p className="text-sm text-gray-500">ID: {usuario.IDUSUARIOWEB}</p>
      </div>
      {!isEditing && (
        <button onClick={onEdit} className="flex-shrink-0 flex items-center gap-1.5 text-sm font-semibold text-ec-primary-600 hover:text-ec-primary-800">
          <Icon name="edit-3" className="h-4 w-4" /> Editar
        </button>
      )}
    </div>
    <div className="border-t border-gray-200 mt-3 pt-3 space-y-2 text-sm">
      {isEditing ? (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Correo electrónico</label>
            <input
              type="email"
              value={nuevoCorreo}
              onChange={(e) => setNuevoCorreo(e.target.value)}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-ec-primary-500"
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={onCancel} className="px-3 py-1.5 text-sm rounded-md border border-gray-300 hover:bg-gray-50">Cancelar</button>
            <button onClick={onSave} className="px-3 py-1.5 text-sm rounded-md bg-ec-primary-600 text-white hover:bg-ec-primary-700">Guardar</button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex justify-between">
            <span className="text-gray-500">Correo:</span>
            <span className="font-medium text-gray-800 text-right break-all">{usuario.CORREO}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Creación:</span>
            <span className="text-gray-600">{formatDateColombia(usuario.FECHACREACION)}</span>
          </div>
        </>
      )}
    </div>
  </div>
);

const UserTableRow = ({ usuario, isEditing, onEdit, onSave, onCancel, nuevoCorreo, setNuevoCorreo }) => (
  <tr className={`transition-colors ${isEditing ? 'bg-ec-primary-50' : 'hover:bg-gray-50'}`}>
    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{usuario.CODIGO_ECCARGO}</td>
    <td className="px-6 py-4 text-sm font-medium text-gray-900">{usuario.NOMBRE_EMPRESA || 'N/A'}</td>
    <td className="px-6 py-4 text-sm text-gray-800">
      {isEditing ? (
        <input
          type="email"
          value={nuevoCorreo}
          onChange={(e) => setNuevoCorreo(e.target.value)}
          className="w-full min-w-[250px] px-3 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-ec-primary-500"
          autoFocus
          onKeyDown={(e) => { if (e.key === 'Enter') onSave() }}
        />
      ) : (
        <span className="break-all">{usuario.CORREO}</span>
      )}
    </td>
    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDateColombia(usuario.FECHACREACION)}</td>
    <td className="px-6 py-4 whitespace-nowrap text-sm">
      <div className="flex items-center gap-3">
        {isEditing ? (
          <>
            <button onClick={onSave} className="p-1.5 rounded-full text-green-600 hover:bg-green-100" title="Guardar"><Icon name="check" className="h-5 w-5" /></button>
            <button onClick={onCancel} className="p-1.5 rounded-full text-red-600 hover:bg-red-100" title="Cancelar"><Icon name="x" className="h-5 w-5" /></button>
          </>
        ) : (
          <button onClick={onEdit} className="flex items-center gap-1.5 text-sm font-semibold text-ec-primary-600 hover:text-ec-primary-800">
            <Icon name="edit-3" className="h-4 w-4" /> Editar
          </button>
        )}
      </div>
    </td>
  </tr>
);

const Pagination = ({ currentPage, totalPages, onPageChange }) => (
  totalPages > 1 && (
    <div className="flex items-center justify-between text-sm">
      <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} className="inline-flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">
        <Icon name="chevron-left" className="h-4 w-4" /> Anterior
      </button>
      <span className="text-gray-700">Página <span className="font-bold">{currentPage}</span> de <span className="font-bold">{totalPages}</span></span>
      <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} className="inline-flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">
        Siguiente <Icon name="chevron-right" className="h-4 w-4" />
      </button>
    </div>
  )
);

const SkeletonLoader = () => (
  <div className="p-4 md:p-0">
    <div className="hidden md:block">
      <div className="h-6 bg-gray-200 rounded w-1/4 mb-4 animate-pulse"></div>
      {[...Array(5)].map((_, i) => <div key={i} className="h-12 bg-gray-100 rounded mb-2 animate-pulse"></div>)}
    </div>
    <div className="md:hidden space-y-3">
      {[...Array(3)].map((_, i) => <div key={i} className="h-28 bg-gray-100 rounded-lg animate-pulse"></div>)}
    </div>
  </div>
);

const AdminUsuarios = () => {
  const { usuarios, loading, error, filtro, setFiltro, actualizarCorreoUsuario } = useAdminUsers();
  const [editingUser, setEditingUser] = useState(null);
  const [nuevoCorreo, setNuevoCorreo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const iniciarEdicion = (usuario) => {
    setEditingUser(usuario.IDUSUARIOWEB);
    setNuevoCorreo(usuario.CORREO);
  };
  const cancelarEdicion = () => {
    setEditingUser(null);
    setNuevoCorreo('');
  };
  const handleSave = async (userId) => {
    const success = await actualizarCorreoUsuario(userId, nuevoCorreo);
    if (success) cancelarEdicion();
  };

  useEffect(() => { setCurrentPage(1) }, [usuarios.length]);

  const paginatedUsers = usuarios.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.ceil(usuarios.length / itemsPerPage);

  return (
    <div className="space-y-6">
      <header className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 bg-ec-primary-100 p-3 rounded-full"><Icon name="users" className="h-6 w-6 text-ec-primary-600" /></div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gestión de Usuarios</h1>
            <p className="text-gray-600 mt-1">Busque y administre los correos electrónicos de los usuarios.</p>
          </div>
        </div>
      </header>

      <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
        <SearchFilter filtro={filtro} setFiltro={setFiltro} disabled={loading} />
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading && <SkeletonLoader />}
        {!loading && error && <div className="text-center py-12 text-red-600">{error}</div>}
        {!loading && !error && usuarios.length === 0 && (
          <div className="text-center py-20">
            <Icon name="search" className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-lg font-semibold text-gray-900">No se encontraron resultados</h3>
            <p className="mt-1 text-sm text-gray-500">{filtro ? 'Intente con otro término de búsqueda.' : 'No hay usuarios para mostrar.'}</p>
          </div>
        )}
        {!loading && !error && usuarios.length > 0 && (
          <>
            <div className="md:hidden space-y-3 bg-gray-50 p-4">
              {paginatedUsers.map(usuario => (
                <UserCard
                  key={usuario.IDUSUARIOWEB}
                  usuario={usuario}
                  isEditing={editingUser === usuario.IDUSUARIOWEB}
                  onEdit={() => iniciarEdicion(usuario)}
                  onSave={() => handleSave(usuario.IDUSUARIOWEB)}
                  onCancel={cancelarEdicion}
                  nuevoCorreo={nuevoCorreo}
                  setNuevoCorreo={setNuevoCorreo}
                />
              ))}
            </div>
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Código</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Empresa</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Correo</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha Creación</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedUsers.map((usuario) => (
                    <UserTableRow
                      key={usuario.IDUSUARIOWEB}
                      usuario={usuario}
                      isEditing={editingUser === usuario.IDUSUARIOWEB}
                      onEdit={() => iniciarEdicion(usuario)}
                      onSave={() => handleSave(usuario.IDUSUARIOWEB)}
                      onCancel={cancelarEdicion}
                      nuevoCorreo={nuevoCorreo}
                      setNuevoCorreo={setNuevoCorreo}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            <div className="bg-white px-4 py-3 border-t border-gray-200">
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminUsuarios;